import { useEffect, useRef, useState } from "react";
import Modal from "../../../../../components/Modal/Modal";
import Button from "../../../../../components/Button/Button";
import LeadStageIcon from "../../../../../assets/svg/lead-stage-icon";
import type { StageInfoRow } from "../utlis/types";

interface LeadStagesInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stages: StageInfoRow[];
}

const StageDescription = ({ description }: { description: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = descRef.current;
    if (el) setIsTruncated(el.scrollHeight > el.clientHeight);
  }, [description]);

  if (!description) {
    return (
      <span className="text-sm italic text-gray-placeholder dark:text-gray-placeholder-dk">
        No description added
      </span>
    );
  }

  return (
    <div className="flex flex-col">
      <p
        ref={descRef}
        className={`text-sm text-verba-indigo dark:text-verba-indigo-dk ${
          isExpanded ? "" : "line-clamp-3"
        }`}
      >
        {description}
      </p>
      {(isTruncated || isExpanded) && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-verba-blue dark:text-verba-blue-dk text-sm font-medium text-left mt-0.5 w-fit cursor-pointer"
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};

const LeadStagesInfoModal = ({
  open,
  onOpenChange,
  stages,
}: LeadStagesInfoModalProps) => {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      customHeader={
        <div className="flex items-center gap-2">
          <LeadStageIcon className="w-[24px] h-[24px] text-gray-placeholder dark:text-gray-placeholder-dk" />
          <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
            Stage Descriptions
          </span>
        </div>
      }
      contentClassName="w-[600px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk backdrop-blur-[10px]"
    >
      <div className="flex flex-col rounded-lg border border-border-gray dark:border-border-gray-dk max-h-[60vh] overflow-y-auto">
        {stages.map((stage) => (
          <div
            key={stage.code}
            className="grid grid-cols-[200px_1fr] items-center gap-3 border-b last:border-b-0 border-border-gray dark:border-border-gray-dk px-[16px] py-[8px]"
          >
            <div
              className="flex items-center rounded-[6px] p-1 px-2 w-fit max-w-full"
              style={{ backgroundColor: stage.color }}
            >
              <span className="text-sm font-medium uppercase text-fixed-white dark:text-fixed-white-dk truncate">
                {stage.name}
              </span>
            </div>
            <StageDescription description={stage.description} />
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          innerText="Okay"
          buttonContainerClass="bg-verba-indigo dark:bg-verba-indigo-dk text-pure-white dark:text-pure-white-dk rounded-lg px-6 py-2 text-sm"
          onClick={() => onOpenChange(false)}
        />
      </div>
    </Modal>
  );
};

export default LeadStagesInfoModal;
